import { useState, useEffect, useMemo, useRef } from 'react';
import { API, formatWeight, formatDateTime, displayDate } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Search, Calendar, ChevronDown, ChevronUp, Filter, X,
  ShoppingCart, Package, Scale, CheckSquare, Plus, Minus,
  GitMerge, FileText, ArrowRightLeft, Box as BoxIcon, CheckCircle, XCircle,
  Clock
} from 'lucide-react';

const EVENT_TYPE_CONFIG = {
  sale: { label: 'Sale', color: 'border-l-rose-500', badge: 'destructive', icon: ShoppingCart },
  sale_return: { label: 'Sale Return', color: 'border-l-pink-400', badge: 'outline', icon: ArrowRightLeft },
  purchase: { label: 'Purchase', color: 'border-l-blue-500', badge: 'default', icon: Package },
  purchase_return: { label: 'Purchase Return', color: 'border-l-sky-400', badge: 'outline', icon: ArrowRightLeft },
  gold_add: { label: 'Gold Added', color: 'border-l-emerald-500', badge: 'outline', icon: Plus },
  gold_subtract: { label: 'Gold Subtracted', color: 'border-l-red-500', badge: 'outline', icon: Minus },
  poly_add: { label: 'Poly Added', color: 'border-l-violet-500', badge: 'outline', icon: Plus },
  poly_subtract: { label: 'Poly Subtracted', color: 'border-l-violet-400', badge: 'outline', icon: Minus },
  stock_snapshot: { label: 'Stock Taking', color: 'border-l-amber-500', badge: 'secondary', icon: Scale },
  four_box_check: { label: '4-Box Check', color: 'border-l-cyan-500', badge: 'secondary', icon: CheckSquare },
  box_created: { label: 'Box Created', color: 'border-l-green-500', badge: 'default', icon: BoxIcon },
  box_approved: { label: 'Approved', color: 'border-l-emerald-500', badge: 'default', icon: CheckCircle },
  box_rejected: { label: 'Rejected', color: 'border-l-red-500', badge: 'destructive', icon: XCircle },
  box_pending: { label: 'Pending Approval', color: 'border-l-amber-500', badge: 'outline', icon: Clock },
  admin_edit: { label: 'Admin Edit', color: 'border-l-yellow-500', badge: 'outline', icon: FileText },
  merge_source: { label: 'Merge (Source)', color: 'border-l-orange-500', badge: 'secondary', icon: GitMerge },
  merge_target: { label: 'Merge (Target)', color: 'border-l-orange-400', badge: 'secondary', icon: GitMerge },
  split_source: { label: 'Split (Source)', color: 'border-l-amber-500', badge: 'secondary', icon: GitMerge },
  split_target: { label: 'Split (Target)', color: 'border-l-amber-400', badge: 'secondary', icon: GitMerge },
};

function getConfig(type) {
  if (EVENT_TYPE_CONFIG[type]) return EVENT_TYPE_CONFIG[type];
  if (type?.includes('merge')) return EVENT_TYPE_CONFIG.merge_source;
  if (type?.includes('split')) return EVENT_TYPE_CONFIG.split_source;
  return { label: type?.replace(/_/g, ' ') || 'Unknown', color: 'border-l-muted-foreground', badge: 'outline', icon: FileText };
}

function formatDateLabel(d) {
  if (!d) return '—';
  const m = String(d).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) {
    try { 
      const dt = new Date(d + 'T00:00:00');
      const weekday = dt.toLocaleDateString('en-IN', { weekday: 'short' });
      return `${m[3]}-${m[2]}-${m[1]} (${weekday})`; 
    } catch { return `${m[3]}-${m[2]}-${m[1]}`; }
  }
  return d;
}

export default function BoxHistoryPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedBox, setSelectedBox] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [typeFilter, setTypeFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [collapsedDates, setCollapsedDates] = useState({});
  const searchRef = useRef(null);
  const dropdownRef = useRef(null);

  // Search boxes as user types
  useEffect(() => {
    if (!searchQuery || searchQuery.length < 1) { setSearchResults([]); return; }
    const timer = setTimeout(async () => {
      try {
        const res = await API.get('/box-history/search', { params: { q: searchQuery } });
        setSearchResults(res.data);
        setShowDropdown(true);
      } catch (e) { console.error(e); }
    }, 250);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
          searchRef.current && !searchRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const selectBox = async (box) => {
    setSelectedBox(box);
    setSearchQuery(box.box_id);
    setShowDropdown(false);
    setLoading(true);
    try {
      const res = await API.get(`/box-history/${encodeURIComponent(box.box_id)}`);
      setTimeline(res.data);
    } catch (e) { toast.error('Failed to load box history'); }
    finally { setLoading(false); }
  };

  // Filter timeline
  const filteredTimeline = useMemo(() => {
    let items = timeline;
    if (typeFilter) {
      items = items.filter(e => {
        if (typeFilter === 'operations') return e.source === 'operation';
        if (typeFilter === 'stock_taking') return e.source === 'stock_taking';
        if (typeFilter === 'four_box') return e.source === 'four_box_check';
        if (typeFilter === 'box_events') return e.source === 'box' || e.source === 'approval';
        return true;
      });
    }
    if (startDate) items = items.filter(e => e.date >= startDate);
    if (endDate) items = items.filter(e => e.date <= endDate);
    return items;
  }, [timeline, typeFilter, startDate, endDate]);

  // Group by date
  const groupedTimeline = useMemo(() => {
    const groups = {};
    filteredTimeline.forEach(ev => {
      const d = ev.date || 'Unknown';
      if (!groups[d]) groups[d] = [];
      groups[d].push(ev);
    });
    return Object.entries(groups)
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([date, events]) => ({ date, events }));
  }, [filteredTimeline]);

  // All unique source types for filter
  const availableTypes = useMemo(() => {
    const sources = new Set(timeline.map(e => e.source));
    return sources;
  }, [timeline]);

  const toggleDateCollapse = (date) => {
    setCollapsedDates(prev => ({ ...prev, [date]: !prev[date] }));
  };

  const clearBox = () => {
    setSelectedBox(null);
    setSearchQuery('');
    setTimeline([]);
    setTypeFilter('');
    setStartDate('');
    setEndDate('');
    setCollapsedDates({});
  };

  const clearFilters = () => { setTypeFilter(''); setStartDate(''); setEndDate(''); };
  const hasFilters = typeFilter || startDate || endDate;

  const renderEventDetail = (ev) => {
    const d = ev.details || {};
    const cfg = getConfig(ev.type);
    const Icon = cfg.icon;

    return (
      <div className={`px-3 sm:px-4 py-2.5 border-l-2 ${cfg.color} hover:bg-muted/20 transition-colors`} data-testid={`timeline-event-${ev.type}`}>
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <Icon size={13} className="text-muted-foreground" />
            <Badge variant={cfg.badge} className="text-[10px] sm:text-xs">{cfg.label}</Badge>
            {ev.source === 'stock_taking' && d.stock_time && (
              <Badge variant="outline" className="text-[10px]">{d.stock_time}</Badge>
            )}
          </div>
          <span className="text-[10px] sm:text-xs text-muted-foreground">
            {ev.timestamp ? new Date(ev.timestamp).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: 'Asia/Kolkata' }) : ''}
          </span>
        </div>

        {ev.source === 'operation' && (
          <div className="mt-1 space-y-1">
            {/* Before/After Weight Bar */}
            {(d.before_gross_g != null || d.after_gross_g != null) && (
              <div className="flex items-center gap-1.5 text-xs font-mono">
                <span className="text-muted-foreground/70 w-12 text-right shrink-0">Before:</span>
                <span className="font-semibold">{formatWeight(d.before_gross_g)}</span>
                <span className="text-muted-foreground/50 px-1">→</span>
                <span className="text-muted-foreground/70 w-10 text-right shrink-0">After:</span>
                <span className="font-semibold">{formatWeight(d.after_gross_g)}</span>
                {d.before_gross_g != null && d.after_gross_g != null && d.before_gross_g !== d.after_gross_g && (
                  <span className={`ml-1 font-semibold ${d.after_gross_g > d.before_gross_g ? 'text-emerald-400' : 'text-rose-400'}`}>
                    ({d.after_gross_g > d.before_gross_g ? '+' : ''}{(d.after_gross_g - d.before_gross_g).toFixed(3)}g)
                  </span>
                )}
              </div>
            )}
            <div className="flex flex-wrap gap-3 text-xs">
              {d.gold_weight_g > 0 && <span>Gold: <b className="weight-value">{formatWeight(d.gold_weight_g)}</b></span>}
              {d.polyethene_weight_g > 0 && <span>Poly: <b className="weight-value">{formatWeight(d.polyethene_weight_g)}</b></span>}
              {d.gold_purity && <span className={`status-badge purity-${d.gold_purity} text-[10px]`}>{d.gold_purity}</span>}
              {d.customer_name && <span className="text-muted-foreground">Customer: {d.customer_name}</span>}
              {d.supplier_name && <span className="text-muted-foreground">Karigar: {d.supplier_name}</span>}
              {d.salesman_name && <span className="text-muted-foreground">Salesman: {d.salesman_name}</span>}
            </div>
          </div>
        )}

        {ev.source === 'stock_taking' && (
          <div className="flex flex-wrap gap-3 text-xs mt-1">
            <span>Gross: <b className="weight-value">{formatWeight(d.gross_weight_g)}</b></span>
            {d.is_verified && <span className="text-emerald-500 flex items-center gap-1"><CheckCircle size={10} /> Verified</span>}
          </div>
        )}

        {ev.source === 'four_box_check' && (
          <div className="text-xs mt-1 space-y-0.5">
            <span className="font-mono text-muted-foreground">{(d.box_ids || []).join(' + ')}</span>
            <div className="flex gap-3">
              <span>Group: <b className="font-mono">{formatWeight(d.group_weight_g)}</b></span>
              <span>Sum: <b className="font-mono">{formatWeight(d.individual_total_g)}</b></span>
              {d.matches
                ? <span className="text-emerald-500 font-semibold">MATCH</span>
                : <span className="text-rose-400 font-semibold">MISMATCH (diff: {formatWeight(d.difference_g)})</span>
              }
            </div>
          </div>
        )}

        {ev.source === 'box' && (
          <div className="flex flex-wrap gap-3 text-xs mt-1">
            <span>Type: <b>{d.box_type}</b></span>
            <span className={`status-badge purity-${d.gold_purity} text-[10px]`}>{d.gold_purity}</span>
            <span>Gross: <b className="weight-value">{formatWeight(d.gross_weight_g)}</b></span>
            {d.created_by && <span className="text-muted-foreground">by {d.created_by}</span>}
          </div>
        )}

        {ev.source === 'approval' && (
          <div className="flex flex-wrap gap-3 text-xs mt-1">
            <span>Status: <b>{d.status}</b></span>
            {d.resolved_by && <span className="text-muted-foreground">by {d.resolved_by}</span>}
            {d.comments && <span className="italic text-muted-foreground">{d.comments}</span>}
          </div>
        )}

        {/* Common: notes and submitted_by */}
        {d.notes && <p className="text-[10px] sm:text-xs text-muted-foreground mt-1 italic">{d.notes}</p>}
        {d.submitted_by && ev.source === 'operation' && <span className="text-[10px] text-muted-foreground mt-0.5 inline-block">by {d.submitted_by}</span>}
      </div>
    );
  };

  return (
    <div data-testid="box-history-page">
      <div className="page-header">
        <h1>Box History</h1>
        <p className="hidden sm:block text-sm text-muted-foreground">Search any box to view its complete action history</p>
      </div>

      {/* Search Bar */}
      <Card className="mb-4">
        <CardContent className="pt-4 pb-4">
          <div className="relative" ref={searchRef}>
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="text"
                  value={searchQuery}
                  onChange={e => { setSearchQuery(e.target.value); if (!e.target.value) clearBox(); }}
                  onFocus={() => searchResults.length > 0 && setShowDropdown(true)}
                  placeholder="Search box ID (e.g. A-1, C-10)"
                  className="pl-10"
                  data-testid="box-history-search"
                />
              </div>
              {selectedBox && (
                <Button variant="ghost" size="sm" onClick={clearBox} className="gap-1 shrink-0" data-testid="box-history-clear">
                  <X size={14} /> Clear
                </Button>
              )}
            </div>

            {/* Dropdown */}
            {showDropdown && searchResults.length > 0 && (
              <div ref={dropdownRef} className="absolute top-full left-0 right-0 mt-1 z-50 bg-popover border rounded-lg shadow-lg max-h-60 overflow-y-auto" data-testid="box-history-dropdown">
                {searchResults.map(box => (
                  <button
                    key={box.box_id}
                    onClick={() => selectBox(box)}
                    className="w-full text-left px-3 py-2.5 hover:bg-muted/50 transition-colors flex items-center justify-between border-b border-border/30 last:border-0"
                    data-testid={`box-result-${box.box_id}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-semibold text-sm">{box.box_id}</span>
                      <span className={`status-badge purity-${box.gold_purity} text-[10px]`}>{box.gold_purity}</span>
                    </div>
                    <span className="text-xs text-muted-foreground truncate max-w-[180px]">{box.description || ''}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Selected Box Info + Filters */}
      {selectedBox && (
        <>
          <Card className="mb-4">
            <CardContent className="pt-3 pb-3 px-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
                <div className="flex items-center gap-2">
                  <BoxIcon size={16} className="text-muted-foreground" />
                  <span className="font-mono font-bold text-lg">{selectedBox.box_id}</span>
                  <span className={`status-badge purity-${selectedBox.gold_purity}`}>{selectedBox.gold_purity}</span>
                  {selectedBox.description && <span className="text-xs text-muted-foreground hidden sm:inline">- {selectedBox.description}</span>}
                </div>
                <span className="text-xs text-muted-foreground">{filteredTimeline.length} events total</span>
              </div>

              {/* Filters */}
              <div className="flex items-center gap-1.5 mb-2">
                <Filter size={14} className="text-muted-foreground" />
                <span className="text-xs font-semibold text-muted-foreground">Filters</span>
                {hasFilters && (
                  <Button variant="ghost" size="sm" className="h-6 px-2 text-xs ml-auto" onClick={clearFilters} data-testid="history-clear-filters">
                    <X size={12} className="mr-1" />Clear
                  </Button>
                )}
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="flex items-center gap-2 flex-1">
                  <div className="flex-1">
                    <label className="text-[10px] text-muted-foreground mb-0.5 block">From</label>
                    <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="h-8 text-xs" data-testid="box-history-start-date" />
                  </div>
                  <span className="text-muted-foreground text-xs mt-4">to</span>
                  <div className="flex-1">
                    <label className="text-[10px] text-muted-foreground mb-0.5 block">To</label>
                    <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="h-8 text-xs" data-testid="box-history-end-date" />
                  </div>
                </div>
                <div className="sm:w-44">
                  <label className="text-[10px] text-muted-foreground mb-0.5 block">Event Type</label>
                  <Select value={typeFilter || 'all'} onValueChange={v => setTypeFilter(v === 'all' ? '' : v)}>
                    <SelectTrigger className="h-8 text-xs" data-testid="box-history-type-filter"><SelectValue placeholder="All events" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All events</SelectItem>
                      {availableTypes.has('operation') && <SelectItem value="operations">Operations (Sales/Purchases)</SelectItem>}
                      {availableTypes.has('stock_taking') && <SelectItem value="stock_taking">Stock Taking</SelectItem>}
                      {availableTypes.has('four_box_check') && <SelectItem value="four_box">4-Box Checks</SelectItem>}
                      {(availableTypes.has('box') || availableTypes.has('approval')) && <SelectItem value="box_events">Box Events (Create/Approve)</SelectItem>}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Timeline */}
          {loading ? (
            <div className="flex items-center justify-center py-16"><div className="text-muted-foreground">Loading history...</div></div>
          ) : groupedTimeline.length > 0 ? (
            groupedTimeline.map(group => (
              <Card key={group.date} className="mb-3" data-testid={`timeline-date-group-${group.date}`}>
                <CardHeader
                  className="pb-0 pt-3 px-3 sm:px-4 cursor-pointer hover:bg-muted/30 rounded-t-lg transition-colors"
                  onClick={() => toggleDateCollapse(group.date)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Calendar size={14} className="text-muted-foreground" />
                      <span className="text-sm font-semibold">{formatDateLabel(group.date)}</span>
                      <span className="text-xs text-muted-foreground">({group.events.length} events)</span>
                    </div>
                    {collapsedDates[group.date] ? <ChevronDown size={16} className="text-muted-foreground" /> : <ChevronUp size={16} className="text-muted-foreground" />}
                  </div>
                </CardHeader>
                {!collapsedDates[group.date] && (
                  <CardContent className="p-0 pt-2">
                    <div className="divide-y divide-border/40">
                      {group.events.map((ev, idx) => (
                        <div key={`${ev.type}-${idx}`}>{renderEventDetail(ev)}</div>
                      ))}
                    </div>
                  </CardContent>
                )}
              </Card>
            ))
          ) : (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground text-sm">
                No events found{hasFilters ? ' for the selected filters' : ' for this box'}
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Empty state */}
      {!selectedBox && (
        <Card>
          <CardContent className="py-16 text-center">
            <Search size={40} className="mx-auto mb-3 text-muted-foreground/40" />
            <p className="text-muted-foreground">Search for a box ID above to view its complete history</p>
            <p className="text-xs text-muted-foreground mt-1">All operations, stock taking, 4-box checks, and more</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
